<template>
  <div id="externalAccountSetup">

    <div class="vo-external-card">

      <div class="vo-body">
        
        <div class="vo-title">{{ title }}</div>
        
        <div class="vo-context">Please fill out the following form to complete your Vilio registration.</div>

        <div class="section-title">Profile</div>
        <div class="section">
          <v-text-field :disabled="lockout" :validate-on-blur="$refs.firstNameInput && $refs.firstNameInput.valid" label="First Name" ref="firstNameInput" type="text" v-model.trim="user.firstName" :rules="rules.name"></v-text-field>  

          <v-text-field :disabled="lockout" :validate-on-blur="$refs.lastNameInput && $refs.lastNameInput.valid" label="Last Name" ref="lastNameInput" type="text" v-model.trim="user.lastName" :rules="rules.name"></v-text-field>

          <v-text-field :disabled="lockout" :validate-on-blur="$refs.phoneInput && $refs.phoneInput.valid" label="Phone #" ref="phoneInput" type="text" v-model.trim="user.phone" :rules="rules.phone"></v-text-field>

          <v-text-field :disabled="lockout" :validate-on-blur="$refs.pass1Input && $refs.pass1Input.valid" label="Password" ref="pass1Input" type="password" v-model="user.password" :rules="rules.pass1"></v-text-field>

          <v-text-field :disabled="lockout" :validate-on-blur="$refs.pass2Input && $refs.pass2Input.valid" label="Confirm Password" ref="pass2Input" type="password" v-model="pass2" :rules="rules.pass2"></v-text-field>
        </div>

        <div class="section-title">
          Notifications
          <div class="icon-wrapper">
            <div class="icon">
              <v-icon>mdi-email</v-icon>
            </div>
            <div class="icon">
              <v-icon>mdi-cellphone</v-icon>
            </div>
          </div>
        </div>
        <div class="section">

          <template v-if="invite">
        
            <div class="noti-container" v-for="(item, key) in notificationSettings" :key="key" v-if="!item.managerOnly || item.managerOnly && !invite.role.hasOwnProperty('unit')">
        
              <div class="noti-title">{{ item.title }}</div>
        
              <div class="noti-checkbox-email">
                <template v-if="'email' in item">
                  <v-checkbox :disabled="lockout" hide-details color="primary" v-model="item.email"></v-checkbox>
                </template>
              </div>
        
              <div class="noti-checkbox-push">
                <template v-if="'push' in item">
                  <v-checkbox :disabled="lockout" hide-details class="checkbox" color="primary" v-model="item.push"></v-checkbox>
                </template>
              </div>
              <div class="clear"></div>
            </div>
          </template>
        
        </div>
    
        <div class="section-title">Terms of Use &amp; Privacy Policy</div>
        <div class="section">

          <div class="tos">
            <div class="tos-title">By checking the box, you agree to our <a :href="policyUrl" target="_blank">Terms of Service &amp; Privacy Policy.</a></div>
            <div class="checkbox">
              <v-checkbox :disabled="lockout" hide-details color="primary" v-model="tos"></v-checkbox>              
            </div>
          </div>

        </div>
      </div>
      
      <v-btn block flat class="vo-card-button" :loading="lockout" v-ripple @click="createAccount">COMPLETE</v-btn>
      
      <!-- HEADER -->
      <vo-external-header :title="title"></vo-external-header>
    </div>

  </div>
</template>


<script>

  export default {
    data () {
      return {
        invite: false,
        title: 'Account Setup',
        user: {
          firstName: '',
          lastName: '',
          phone: '',
          password: ''
        },
        tos: false,
        pass1: '',
        pass2: '',
        lockout: false,
        notificationSettings: {

          // Web is always true when it exists, but doesn't exist on every notification type
          // managerOnly and Title only exist to help with templating login, and are stripped out when saving to the account
          ticketCreation: {
            title: 'New Ticket',
            push: true,
            email: false,
            web: true
          },
          ticketComment: {
            title: 'New Chat Message',            
            push: true,
            email: false,
            web: true
          },
          ticketNote: {
            managerOnly: true,
            title: 'New Ticket Note',            
            push: true,
            email: false,
            web: true
          },
          ticketStatusChanged: {
            title: 'Ticket Status Updates',            
            push: true,
            email: false,
            web: true
          },
          ticketAssigneeChanged: {
            title: 'Ticket Assignee Updates',            
            push: true,
            email: false,
            web: true
          },
          ticketTagUpdated: {
            managerOnly: true,
            title: 'Ticket Tag Updates',            
            push: true,
            email: false,
            web: true
          },
          ticketResolved: {
            title: 'Ticket Resolved',            
            email: false,
            push: true
          },
          dailyEmail: {
            managerOnly: true,
            title: 'Daily Summary',            
            email: false
          }
        },
        rules: {
          name: [
            function (e) {
              return (e.length < 3) ? 'Please enter a name.' : true;
            }
          ],
          phone: [
            function (e) {
              return (!window.regex.phone.test(e)) ? 'Please enter a valid phone number.' : true;
            }
          ],
          pass1: [
            function (e) {
              return (!e || e.length < 6) ? 'Password must be at least 6 characters long.' : true;
            }.bind(this)
          ],
          pass2: [
            function (e) {
              return (this.user.password !== this.pass2) ? 'Passwords do not match.' : true;
            }.bind(this)
          ]
        }
      }
    },

    computed: {
      
      policyUrl () {
        return EXTERNAL_URLS.policy;
      }

    },

    methods: {
      
      
      // createAccount() - Validates form, returns errors if applicable, accepts invite if validation passes
      createAccount () {
        if (this.lockout) return;
        this.lockout = true;

        var valid = this.validateForm();
        
        // Input Errors
        if (!valid) { 
          Toast.show('Please correct the highlighted errors.', 'error');
          this.lockout = false;
          return;
        }

        // TOS not checked
        if (!this.tos) {
          Toast.show('You must agree to the Terms of Service and Privacy Policy to continue.', 'error');
          this.lockout = false;
          return;
        }

        // Accept Invite
        this.$store.dispatch('acceptInvite', {
          user: this.user,
          notificationSettings: this.parseNotificationSettings(),
          token: this.invite.token
        })
        .then(data => {
          
          this.lockout = false;

          Dialog.showDialog({
            title: 'Account Created',
            body: 'Your account has been created! You will now be directed to the login page, where you may sign in with your email address and password.',
            confirm: {
              title: 'Let\'s Go',
              action () {
                window.open(APP_URL, '_self');
                return Promise.resolve();
              }
            },
            modal: true
          });

        })
        .catch(e => {
          this.lockout = false;
          console.log('Error:', e);
          Toast.showError(e);
        });

      },

      // validateForm() - Tells input fields to validate themselves, returns whether or not ALL fields are valid
      validateForm () {
        this.$refs.firstNameInput.validate(true);
        this.$refs.lastNameInput.validate(true);
        this.$refs.phoneInput.validate(true);
        this.$refs.pass1Input.validate(true);
        this.$refs.pass2Input.validate(true);

        return (this.$refs.firstNameInput.valid && this.$refs.lastNameInput && this.$refs.phoneInput.valid && this.$refs.pass1Input.valid && this.$refs.pass2Input.valid);
      },

      // parseNotificationSettings() - Loops through all notification settings available to this role, returns an object the server expects
      parseNotificationSettings () {

        var notificationSettings = {};

        for (var key in this.notificationSettings) {

          if (!this.notificationSettings[key].managerOnly || (this.notificationSettings[key].managerOnly && !this.invite.role.hasOwnProperty('unit'))) {

            var stripped = {};
            for (var prop in this.notificationSettings[key]) {
              
              // Copy every property except managerOnly and title which are only used for front end templating
              if (prop !== 'managerOnly' && prop !== 'title') {
                stripped[prop] = this.notificationSettings[key][prop];
              }
            }

            // Save the stripped version
            notificationSettings[key] = stripped;
          }
        }

        return notificationSettings;
      },

      // initForm() - Initializes all inputs as valid
      initForm () {
        this.$refs.firstNameInput.valid = true;
        this.$refs.lastNameInput.valid  = true;
        this.$refs.phoneInput.valid     = true;
        this.$refs.pass1Input.valid     = true;
        this.$refs.pass2Input.valid     = true;
      },

      // fetchInviteFromToken(token) - Fetches invite from server using token, calls appropriate functions based on result 
      fetchInviteFromToken (token) {

        this.$store.dispatch('loadInviteByToken', {
          token: token
        })
        .then(data => {
          this.handleInvite(data);
        })
        .catch(e => {
          this.handleInvalidToken();
        });
      },

      // handleInvite(invite) - Checks invite state for validity, populates this.invite if user is able to continue
      handleInvite (invite) {
          
        if (invite.accepted) {
          Dialog.showDialog({
            title: 'Invite Already Accepted',
            body: 'This invitation has already been accepted. You will now be directed to the login page. If you can\'t remember your password, you can request a new one to be sent to your email address.',
            confirm: {
              title: 'Got It',
              action () {
                window.open(APP_URL, '_self');
                return Promise.resolve();
              }
            },
            modal: true
          });
        }
        else {
          this.invite = invite;
        }
      },

      handleInvalidToken () {

        Dialog.showDialog({
          title: 'Token Invalid',
          body: 'Your account setup token is invalid. Please contact your property manager for more information.',
          confirm: {
            title: 'Got It',
            action () {
              window.open(APP_URL, '_self');
              return Promise.resolve();
            }
          },
          modal: true
        });
      
      }
    },

    mounted () {

      // Default all fields to valid (takes 1 tick for template to render them)
      this.$nextTick(() => {
        this.initForm();
      });

      // Make sure the user had a token
      if (!this.$route.query || this.$route.query && !this.$route.query.token) {
        this.handleInvalidToken();
      }
      else {
        this.fetchInviteFromToken(this.$route.query.token);
      }

    }
  }

</script>

<style lang="scss">

  @import '../css/global.scss';

  #externalAccountSetup {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    @include scrollable;

    .section-title {
      @include fontPrimary400;
      font-size: 20px;
      border-bottom: 1px solid rgba(0, 0, 0, .24);
      padding: 0 0 8px;
      width: 100%;

      .icon-wrapper {

        float: right;
        padding: 0 24px 0 0;

        .icon {
          float: right;
          width: 40px;
        }

      }
    }

    .section {
      padding: 16px;
      box-sizing: border-box;

      .noti-container {

        position: relative;
        margin: 0 0 16px;

        .noti-title {
          width: calc(100% - 90px);
        }

        .noti-checkbox-email {
          position: absolute;
          right: 16px;
          top: 50%;
          transform: translate3d(0, -50%, 0);
        }
        
        .noti-checkbox-push {
          position: absolute;
          right: 56px;
          top: 50%;
          transform: translate3d(0, -50%, 0);
        }
      }

      .tos {
        position: relative;
        
        .tos-title {
          width: calc(100% - 58px);
        }

        .checkbox {
          position: absolute;
          top: 50%;
          right: 8px;
          transform: translate3d(0, -50%, 0);
        }
      }

    }
  }

  @media screen and (max-width: 780px) {
   
    #externalAccountSetup {
      
      background: $lightGreen !important;

      .vo-title {
        display: none;
      }
    }

  }

</style>


// WEBPACK FOOTER //
// src/components/ExternalAccountSetup.vue